import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch } from '../../../hooks/redux';
import { setWheelSelected } from '../../../store/wheel.slice';
import { IJournalResult } from '../../../store/types';
import { useFormFactor } from '../../../utils/formFactor';

import { createDefaultWheelConfig, normalizeWheelConfig, type WheelConfig } from './wheel-config';
import { WheelPanel } from './wheelPanel';

export interface ILifeBalanceWheelClient {
  result?: IJournalResult;
  setResult?: React.Dispatch<React.SetStateAction<IJournalResult>>;
  sendWheelChange?: (values: number[]) => void;
  sendWheelResult?: (values: number[]) => void;
  wheelBootstrap?: {
    nonce: number;
    values: number[];
    sectorNames?: string[];
    isRealtimeVisible?: boolean;
    isSelectionUnlocked?: boolean;
  } | null;
  onWheelBootstrapConsumed?: () => void;
  /** Мобильный 1:1 на весь экран: заголовок лежит в верхней полосе сцены, колесо в светлой карточке */
  mobile?: boolean;
}

export const LifeBalanceWheelClient: FC<ILifeBalanceWheelClient> = ({
  sendWheelChange,
  sendWheelResult,
  wheelBootstrap,
  onWheelBootstrapConsumed,
  mobile = false,
}) => {
  const [active, setActive] = useState(true);
  const [wheelConfig, setWheelConfig] = useState<WheelConfig>(() => createDefaultWheelConfig());
  const [isRealtimeVisible, setIsRealtimeVisible] = useState(true);
  const [isSelectionUnlocked, setIsSelectionUnlocked] = useState(false);
  const lastSentAtRef = useRef(0);
  const pendingValuesRef = useRef<number[] | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dispatch = useAppDispatch();
  const formFactor = useFormFactor();
  const EMIT_THROTTLE_MS = 80;

  const areValuesEqual = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => v === b[i]);

  const sendAction = useCallback(
    (action: string, values: number[]) => {
      if (action === 'change_wheel') {
        sendWheelChange?.(values);
      } else if (action === 'result_wheel') {
        sendWheelResult?.(values);
      }
    },
    [sendWheelChange, sendWheelResult],
  );

  const flushPending = useCallback(() => {
    if (!pendingValuesRef.current) return;
    sendAction('change_wheel', pendingValuesRef.current);
    lastSentAtRef.current = Date.now();
    pendingValuesRef.current = null;
    flushTimerRef.current = null;
  }, [sendAction]);

  useEffect(() => {
    return () => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!wheelBootstrap) return;
    const next = normalizeWheelConfig(wheelBootstrap.values, wheelBootstrap.sectorNames);
    setWheelConfig(next);
    if (typeof wheelBootstrap.isRealtimeVisible === 'boolean') {
      setIsRealtimeVisible(wheelBootstrap.isRealtimeVisible);
    }
    if (typeof wheelBootstrap.isSelectionUnlocked === 'boolean') {
      setIsSelectionUnlocked(wheelBootstrap.isSelectionUnlocked);
    }
    dispatch(setWheelSelected(next.values));
    onWheelBootstrapConsumed?.();
  }, [wheelBootstrap, dispatch, onWheelBootstrapConsumed]);

  const onChange = useCallback(
    (values: number[]) => {
      if (areValuesEqual(values, wheelConfig.values)) return;
      setWheelConfig((prev) => normalizeWheelConfig(values, prev.sectorNames));
      const now = Date.now();
      const diff = now - lastSentAtRef.current;
      if (diff >= EMIT_THROTTLE_MS) {
        sendAction('change_wheel', values);
        lastSentAtRef.current = now;
        pendingValuesRef.current = null;
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        return;
      }

      pendingValuesRef.current = values;
      if (!flushTimerRef.current) {
        flushTimerRef.current = setTimeout(flushPending, EMIT_THROTTLE_MS - diff);
      }
    },
    [wheelConfig.values, sendAction, flushPending],
  );

  const onReady = () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    if (pendingValuesRef.current) {
      sendAction('change_wheel', pendingValuesRef.current);
      pendingValuesRef.current = null;
    }
    setActive(false);
    sendAction('result_wheel', wheelConfig.values);
    dispatch(setWheelSelected(wheelConfig.values));
  };

  const wheelPanel = active ? (
    <WheelPanel
      values={wheelConfig.values}
      sectorNames={wheelConfig.sectorNames}
      isClient
      selectionUnlocked={isSelectionUnlocked}
      clientStatusMessage={!isSelectionUnlocked ? 'Психолог настраивает колесо баланса' : undefined}
      setValues={onChange}
      setReady={onReady}
    />
  ) : (
    <WheelPanel values={wheelConfig.values} sectorNames={wheelConfig.sectorNames} isClient />
  );

  if (mobile) {
    return <div className="w-full h-full flex flex-col bg-ink-muted px-3 pb-3 pt-1">{wheelPanel}</div>;
  }

  if (formFactor === 'mobile-portrait') {
    return (
      <div className="relative flex h-full min-h-0 w-full flex-row gap-[23px] overflow-hidden pt-[8rem]">
        {wheelPanel}
      </div>
    );
  }
  if (formFactor === 'mobile-landscape') {
    return <div className="relative flex h-full min-h-0 w-full flex-row gap-[23px] overflow-hidden">{wheelPanel}</div>;
  }
  return <div className="relative flex h-full min-h-0 w-full flex-row gap-[23px] overflow-hidden">{wheelPanel}</div>;
};
